//@ts-ignore
import { onlyNumeric } from '@origin-digital/normalize';
import { Button, Box, Text, LinkText } from '@origin-digital/style-guide';
import { Field, Form } from 'formik';
import React from 'react';

import analytics from '../../../../../analytics';
import FormRow from '../../../../../components/Form/Fields/FormRow';
import TextField from '../../../../../components/Form/Fields/TextField';
import SelectField from '../../../../../components/Form/Fields/SelectField';
import TextAreaField from '../../../../../components/Form/Fields/TextAreaField';
import Checkbox from '../../../../../components/Form/Fields/Checkbox';
import Address from '../../../../../components/Form/Fields/Address';

import { FieldsProps } from './Fields.types';
import { CHECK_REPAIR_SERVICEABILITY } from '../../../../../queries';

export type RequestServiceFieldProps = {
  productType?: string;
  asyncFetching: boolean;
  asyncSuccess?: boolean;
  graphError: boolean;
  graphMessage: string;
  showManualAddress: boolean;
  isValid: boolean;
  enterAddressManually: (value: boolean) => void;
  setProductType?: (type: string) => void;
  setAsyncFetching: (value: boolean) => void;
  setDisqualification: (value: boolean) => void;
  onGraphError: (value: boolean, errorMessage?: string) => void;
  onFieldChange: (fieldName: string, value: string) => void;
} & FieldsProps;

const Fields: React.StatelessComponent<RequestServiceFieldProps> = ({ ...props }: RequestServiceFieldProps) => {
  const {
    metaData,
    isSubmitting,
    productType,
    setProductType,
    asyncFetching,
    setAsyncFetching,
    setDisqualification,
    onGraphError,
    graphMessage,
    graphError,
    onFieldChange,
    asyncSuccess,
    showManualAddress,
    enterAddressManually,
    isValid
  } = props;

  return (
    <Form>
      <FormRow
        content={metaData.form.content.enquiryType}
        byline={metaData.form.content.enquiryType.byline}
        rowMarginBottom={6}
      >
        <Field
          name={metaData.form.fields.enquiryType.name}
          component={SelectField}
          onFieldChange={(name: any, value: string) => {
            if (setProductType) {
              setProductType(value);
            }
          }}
          label={metaData.form.fields.enquiryType.label}
          options={metaData.form.fields.enquiryType.options}
        />
        <Field
          name={metaData.form.fields.comment.name}
          maxLength={3000}
          component={TextAreaField}
          label={metaData.form.fields.comment.label}
          analytics={false}
        />
      </FormRow>

      <FormRow
        content={metaData.form.content.yourDetails}
        byline={metaData.form.content.yourDetails.byline}
        rowMarginBottom={6}
      >
        <Field
          name={metaData.form.fields.firstName.name}
          label={metaData.form.fields.firstName.label}
          component={TextField}
          analytics={false}
        />
        <Field
          name={metaData.form.fields.lastName.name}
          label={metaData.form.fields.lastName.label}
          component={TextField}
          analytics={false}
        />
        <Field
          name={metaData.form.fields.email.name}
          label={metaData.form.fields.email.label}
          component={TextField}
          analytics={false}
        />
        <Field
          name={metaData.form.fields.phone.name}
          component={TextField}
          helpText={metaData.form.fields.phone.helpText}
          label={metaData.form.fields.phone.label}
          transform={onlyNumeric}
          maxLength={10}
          analytics={false}
        />
      </FormRow>

      <Address
        metaData={metaData}
        productType={productType}
        isValid={asyncSuccess}
        onFieldChange={onFieldChange}
        asyncFetching={asyncFetching}
        setAsyncFetching={setAsyncFetching}
        graphQuery={CHECK_REPAIR_SERVICEABILITY}
        graphError={graphError}
        graphMessage={graphMessage}
        onGraphError={onGraphError}
        showManualAddress={showManualAddress}
        enterAddressManually={enterAddressManually}
        disqualificationMessage={metaData.form.content.disqualification}
        setDisqualification={setDisqualification}
      />

      <Box marginTop={2} marginBottom={4}>
        <Text size={'sm'}>{metaData.form.content.allowMarketingByline}</Text>
      </Box>
      <Field
        name={metaData.form.fields.allowMarketingCommunications.name}
        id={metaData.form.fields.allowMarketingCommunications.name}
        component={Checkbox}
        label={metaData.form.fields.allowMarketingCommunications.label}
      />
      <Box marginTop={10}>
        <Button
          type="submit"
          data-id="LeaveYourDetailsButton"
          text={metaData.form.buttons.submit}
          disabled={!isValid || asyncFetching}
          spinner={isSubmitting}
          onClick={() => {
            analytics.buttonClick('submit', 'General enquiry');
          }}
        />
      </Box>
      <Box marginTop={10}>
        <Text size={'sm'}>
          {metaData.form.content.privacyStatement.byline}
          <Box marginLeft={1} display={'inlineBlock'}>
            <LinkText text={metaData.form.content.privacyStatement.linkText} href={'/privacy.html'} />
          </Box>
        </Text>
      </Box>
    </Form>
  );
};

export { Fields as default };
